<template>
	<div class="ks-tree-node">
		<div
			class="ks-tree-node-row"
			:class="rowClasses"
			:style="rowStyle"
			@click="onRowClick"
		>
			<div class="ks-tree-node-toggle" @click.stop="onToggleClick">
				<span v-if="hasChildren || canLoadChildren" class="ks-tree-node-toggle-icon material-icons">
					{{ nodeIsOpen ? 'expand_more' : 'chevron_right' }}
				</span>
				<span v-else class="ks-tree-node-toggle-spacer"></span>
			</div>
			<div v-if="tree.selectable" class="ks-tree-node-checkbox" @click.stop="onCheckboxClick">
				<div class="ks-checkbox">
					<input type="checkbox" :checked="nodeIsSelected" :indeterminate.prop="nodeIsIndeterminate" />
					<label class="box-label" :class="{ disabled: nodeIsDisabled }"></label>
				</div>
			</div>
			<slot name="prepend" :item="item" :open="nodeIsOpen" :leaf="isLeaf" :selected="nodeIsSelected" :active="nodeIsActive"></slot>
			<div class="ks-tree-node-label">
				<slot name="label" :item="item" :leaf="isLeaf" :selected="nodeIsSelected" :active="nodeIsActive" :open="nodeIsOpen">
					{{ item[tree.itemText] }}
				</slot>
			</div>
			<slot name="append" :item="item" :open="nodeIsOpen" :leaf="isLeaf" :selected="nodeIsSelected" :active="nodeIsActive"></slot>
			<div v-if="tree.removable && !nodeIsDisabled" class="ks-tree-node-remove" @click.stop="onRemoveClick">
				<span class="material-icons ks-tree-node-remove-icon">close</span>
			</div>
		</div>
		<div v-if="nodeIsOpen && isLoading" class="ks-tree-node-loading">
			<span class="material-icons ks-tree-spin">refresh</span>
		</div>
		<div v-if="nodeIsOpen && !isLoading && children.length > 0" class="ks-tree-node-children">
				<k-tree-node
				v-for="child in children"
				:key="child[tree.itemKey]"
				:item="child"
				:level="level + 1"
			>
				<template v-if="$scopedSlots.prepend" v-slot:prepend="props">
					<slot name="prepend" v-bind="props"></slot>
				</template>
				<template v-if="$scopedSlots.label" v-slot:label="props">
					<slot name="label" v-bind="props"></slot>
				</template>
				<template v-if="$scopedSlots.append" v-slot:append="props">
					<slot name="append" v-bind="props"></slot>
				</template>
			</k-tree-node>
		</div>
	</div>
</template>

<script>
export default {
	name: 'k-tree-node',

	inject: ['kTree'],

	props: {
		item: { type: Object, required: true },
		level: { type: Number, default: 0 }
	},

	data: function () {
		return {
			isLoading: false,
			indentation: 10
		}
	},

	computed: {
		tree: function () {
			return this.kTree
		},
		nodeKey: function () {
			return String(this.item[this.tree.itemKey])
		},
		children: function () {
			return this.item[this.tree.itemChildren] || []
		},
		hasChildren: function () {
			return this.children.length > 0
		},
		canLoadChildren: function () {
			return typeof this.tree.loadChildren === 'function' &&
				Array.isArray(this.item[this.tree.itemChildren]) &&
				this.item[this.tree.itemChildren].length === 0
		},
		isLeaf: function () {
			return !this.hasChildren && !this.canLoadChildren
		},
		nodeIsOpen: function () {
			// Access revision to track changes
			var r = this.tree.revision
			return this.tree.isOpen(this.nodeKey)
		},
		nodeIsActive: function () {
			var r = this.tree.revision
			return this.tree.isActive(this.nodeKey)
		},
		nodeIsDisabled: function () {
			return !!this.item[this.tree.itemDisabled]
		},
		nodeIsSelected: function () {
			var r = this.tree.revision
			if (this.tree.selectionType === 'leaf') {
				if (this.isLeaf) {
					return this.tree.isSelected(this.nodeKey)
				}
				var leafKeys = this._collectLeafKeys(this.children)
				var self = this
				return leafKeys.length > 0 && leafKeys.every(function (k) { return self.tree.isSelected(k) })
			}
			if (this.tree.selectionType === 'cascade') {
				if (this.tree.isSelected(this.nodeKey)) return true
				if (!this.isLeaf) {
					return this._hasAnySelectedDescendant(this.children)
				}
				return false
			}
			return this.tree.isSelected(this.nodeKey)
		},
		nodeIsIndeterminate: function () {
			if (this.isLeaf) return false
			if (this.tree.selectionType === 'leaf') {
				var r = this.tree.revision
				var leafKeys = this._collectLeafKeys(this.children)
				var self = this
				var count = leafKeys.filter(function (k) { return self.tree.isSelected(k) }).length
				return count > 0 && count < leafKeys.length
			}
			return false
		},
		rowClasses: function () {
			return {
				'ks-tree-node-active': this.nodeIsActive,
				'ks-tree-node-selected': this.nodeIsSelected,
				'ks-tree-node-disabled': this.nodeIsDisabled,
				'ks-tree-node-rounded': this.tree.rounded,
				'ks-tree-node-shaped': this.tree.shaped
			}
		},
		rowStyle: function () {
			return { paddingLeft: (this.level * this.indentation) + 'px' }
		}
	},

	methods: {
		_collectLeafKeys: function (items) {
			var keys = []
			var self = this
			items.forEach(function (item) {
				var ch = item[self.tree.itemChildren] || []
				if (ch.length === 0) {
					keys.push(String(item[self.tree.itemKey]))
				} else {
					keys = keys.concat(self._collectLeafKeys(ch))
				}
			})
			return keys
		},
		_collectAllKeys: function (items) {
			var keys = []
			var self = this
			items.forEach(function (item) {
				keys.push(String(item[self.tree.itemKey]))
				var ch = item[self.tree.itemChildren] || []
				if (ch.length > 0) {
					keys = keys.concat(self._collectAllKeys(ch))
				}
			})
			return keys
		},
		_hasAnySelectedDescendant: function (items) {
			var self = this
			for (var i = 0; i < items.length; i++) {
				var key = String(items[i][self.tree.itemKey])
				if (self.tree.isSelected(key)) return true
				var ch = items[i][self.tree.itemChildren] || []
				if (ch.length > 0 && self._hasAnySelectedDescendant(ch)) return true
			}
			return false
		},
		onToggleClick: function () {
			if (this.nodeIsDisabled) return
			if (!this.nodeIsOpen && this.canLoadChildren) {
				var self = this
				this.isLoading = true
				Promise.resolve(this.tree.loadChildren(this.item)).then(function () {
					self.isLoading = false
					self.tree.toggleOpen(self.nodeKey)
				}).catch(function () {
					self.isLoading = false
				})
			} else {
				this.tree.toggleOpen(this.nodeKey)
			}
		},
		onRowClick: function () {
			if (this.nodeIsDisabled) return
			if (this.tree.openOnClick && (this.hasChildren || this.canLoadChildren)) {
				this.onToggleClick()
			} else if (this.tree.activatable) {
				this.tree.handleNodeClick(this.item)
			}
		},
		onRemoveClick: function () {
			if (this.nodeIsDisabled) return
			this.tree.handleRemove({
				key: this.nodeKey,
				item: this.item,
				descendantKeys: this.isLeaf ? [] : this._collectAllKeys(this.children),
				leafKeys: this._collectLeafKeys(this.children)
			})
		},
		onCheckboxClick: function () {
			if (this.nodeIsDisabled) return
			if (this.tree.selectionType === 'leaf') {
				if (this.isLeaf) {
					this.tree.handleSelect({
						leaf: true,
						key: this.nodeKey,
						item: this.item
					})
				} else {
					this.tree.handleSelect({
						leaf: false,
						key: this.nodeKey,
						item: this.item,
						leafKeys: this._collectLeafKeys(this.children),
						select: !this.nodeIsSelected
					})
				}
			} else if (this.tree.selectionType === 'cascade') {
				this.tree.handleSelect({
					key: this.nodeKey,
					item: this.item,
					cascade: true,
					descendantKeys: this.isLeaf ? [] : this._collectAllKeys(this.children)
				})
			} else {
				this.tree.handleSelect({
					leaf: this.isLeaf,
					key: this.nodeKey,
					item: this.item,
					independent: true
				})
			}
		}
	}
}
</script>
